import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://nuxcmldfkblaatuhtabh.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_88g1V-6my7-Y9899Sb17tA_YOxi0P2M';

/** Postgres table that holds every live student record. */
export const STUDENTS_TABLE = 'students';

/** Project reference shown in the UI for transparency. */
export const SUPABASE_PROJECT_REF = 'nuxcmldfkblaatuhtabh';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
});

/** SQL a project owner runs once so the app has a table to write into. */
export const STUDENTS_TABLE_SQL = `create table if not exists public.students (
  id uuid primary key default gen_random_uuid(),
  owner_id uuid not null references auth.users on delete cascade,
  full_names text default '',
  gender text default '',
  class_name text default '',
  father_name text default '',
  father_phone text default '',
  mother_name text default '',
  mother_phone text default '',
  note text default '',
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now()
);

alter table public.students enable row level security;

create policy "Students are private to their owner"
  on public.students for all
  using (auth.uid() = owner_id)
  with check (auth.uid() = owner_id);`;

function errorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { code?: string | number };
    if (candidate.code !== undefined) return String(candidate.code);
  }
  return '';
}

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { message?: string };
    if (candidate.message) return candidate.message;
  }
  return String(error ?? '');
}

/** True when the `students` table has not been created in the project yet. */
export function isMissingTableError(error: unknown): boolean {
  const code = errorCode(error);
  const message = errorMessage(error).toLowerCase();
  return code === '42P01' || code === 'PGRST205' || message.includes('does not exist');
}

/** True when row level security blocked the request. */
export function isPermissionError(error: unknown): boolean {
  const code = errorCode(error);
  return code === '42501' || code === 'PGRST301' || code === '401' || code === '403';
}

/** Turns Supabase / Postgres errors into copy a user can act on. */
export function describeSupabaseError(error: unknown): string {
  const code = errorCode(error);
  const message = errorMessage(error);

  if (isMissingTableError(error)) {
    return 'Your school database has not been set up yet. An administrator needs to run the one-time setup shown on the dashboard.';
  }
  if (isPermissionError(error)) {
    return 'This account is not allowed to open these records. Ask your school administrator to check the access rules.';
  }
  if (/failed to fetch|network|load failed/i.test(message)) {
    return 'Could not reach the server from this network. Check your connection and try again.';
  }
  if (code === '23503') {
    return 'This record belongs to an account that no longer exists.';
  }
  if (/invalid api key|jwt/i.test(message)) {
    return 'Your session has expired. Please log out and sign in again.';
  }
  if (/provider is not enabled/i.test(message)) {
    return 'Google sign-in is not switched on for this app yet. Please contact your school administrator.';
  }
  if (/redirect/i.test(message)) {
    return 'This web address is not approved for sign-in yet. Please contact your school administrator.';
  }
  // Never surface infrastructure vendor names to school users.
  const sanitised = message.replace(/supabase|postgrest|postgres/gi, 'the server').trim();
  return sanitised || 'Something went wrong. Please try again.';
}

/**
 * Google's OAuth screen cannot run inside an iframe (preview panes, embeds).
 * Detect it so we can hand the user a real browser tab instead.
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Round-trips to Supabase so the UI can report the true connection state. */
export async function testSupabaseConnection(): Promise<{ ok: boolean; detail: string }> {
  try {
    const { error } = await supabase.from(STUDENTS_TABLE).select('id', { count: 'exact', head: true });
    if (error) return { ok: false, detail: describeSupabaseError(error) };
    return { ok: true, detail: 'Connected — your records are saving to and loading from the school database.' };
  } catch (error) {
    return { ok: false, detail: describeSupabaseError(error) };
  }
}
